import { Container, type Ticker } from 'pixi.js';
import { Easings, TweenGroup } from '../../core/Tween';
import { WinParticles } from '../../effects/WinParticles';
import { CoinAmountLabel } from '../../widgets/CoinAmountLabel';
import type { WheelSettlement } from '../../widgets/wheel/settleWheel';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

/**
 * Wheel-specific win celebration: a "+N <coin>" popup that pops in then drifts
 * up, plus a particle burst sized to the win. Centred on the wheel.
 *
 * <p>Distinct from {@link ./WinSequenceComponent slot's WinSequenceComponent}
 * because the wheel's animation is tuned slightly differently — bigger
 * popup font, longer fade-out, more particles for jackpot wins.
 */
export interface WheelWinSequenceOptions {
  /** Popup fill colour. Defaults to warm gold. */
  fillColor?: number;
  /** Popup stroke colour. Defaults to brand pink. */
  strokeColor?: number;
}

export class WheelWinSequenceComponent extends Container implements SceneComponent {
  private readonly popup: CoinAmountLabel;
  private readonly particles: WinParticles;
  private readonly tweens = new TweenGroup();
  private popupBaseY = 0;

  constructor(ticker: Ticker, options: WheelWinSequenceOptions = {}) {
    super();

    this.particles = new WinParticles(ticker);
    this.addChild(this.particles);

    this.popup = new CoinAmountLabel('', {
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 32,
        fontWeight: '900',
        fill: options.fillColor ?? 0xfff59d,
        align: 'center',
        stroke: { color: options.strokeColor ?? 0xff6bd6, width: 4 },
      },
    });
    this.popup.alpha = 0;
    this.addChild(this.popup);
  }

  layout(ctx: SceneLayoutContext): void {
    const cx = ctx.centerX;
    // Wheel is centred between machineTop and machineBottom; popup sits at
    // wheel centre.
    const cy = Math.round((ctx.machineTop + ctx.machineBottom) / 2);
    this.particles.x = cx;
    this.particles.y = cy;
    this.popup.x = cx;
    this.popup.y = cy;
    this.popupBaseY = cy;
  }

  destroy(): void {
    this.tweens.cancelAll();
    this.particles.destroyEffect();
    super.destroy({ children: true });
  }

  playWin(settlement: WheelSettlement): void {
    const particleCount = Math.min(220, 50 + Math.floor(settlement.winnings * 0.4));
    this.particles.burst(particleCount, {
      tints: [0xff6bd6, 0xfff59d, 0x42e2f5, 0xffd23b, 0xff8a3d, 0xff3b6b],
      lifeMs: 1400,
      minSpeed: 240,
      maxSpeed: 620,
      gravity: 600,
      radius: 5,
    });

    this.popup.setText(`+${settlement.winnings.toLocaleString()}`);
    this.popup.alpha = 0;
    this.popup.scale.set(0.6);
    this.popup.y = this.popupBaseY;

    this.tweens.start({
      durationMs: 240,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.popup.alpha = p;
        this.popup.scale.set(0.6 + p * 0.7);
      },
      onComplete: () => {
        this.tweens.start({
          durationMs: 1100,
          easing: Easings.easeOutCubic,
          onUpdate: (p) => {
            this.popup.alpha = 1 - p;
            this.popup.y -= 0.5;
          },
        });
      },
    });
  }
}
